/**
 * Admin LicenseDetail page — locked-model 4-tab view.
 *
 * Per `vloud/docs/vloud-license-server-v2-plan.md §5`. Reachable at
 * `/licenses/:id`. Sister to the existing drawer view in
 * `Licenses.tsx` — the drawer is "quick triage from the master list",
 * this page is "deep dive when you need every dimension".
 *
 * Tabs:
 *   1. Overview      — customer / license / billing / locked-model limits
 *   2. Capacity      — instances + linux_users history (chart + samples)
 *   3. Heartbeats    — recent heartbeat samples (delegates to existing
 *                       LicensesPage drawer's heartbeat fetch — kept
 *                       minimal here; deeper view follows in C.5)
 *   4. Lifecycle     — license_lifecycle_events timeline
 *
 * Action buttons (top-right): Refresh · Restore · Suspend · Revoke.
 */

import { type JSX, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AdminApiClient,
  type LicenseDetail as LicenseDetailRow,
  type LicenseCapacityResponse, type CapacitySnapshot,
  type LifecycleEvent, type LifecycleEventType,
} from '../api';
import { useSession } from '../session';
import { usePermissions } from '../lib/permissions';
import { failureFrom, userMessageFor } from '../lib/api-state';
import { AccessNotice } from '../components/ui/AccessNotice';
import { SecretValue } from '../components/ui/SecretValue';
import { Button } from '../components/ui/Button';

type TabId = 'overview' | 'capacity' | 'heartbeats' | 'lifecycle';

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview',   label: 'Overview'   },
  { id: 'capacity',   label: 'Capacity'   },
  { id: 'heartbeats', label: 'Heartbeats' },
  { id: 'lifecycle',  label: 'Lifecycle'  },
];

export function LicenseDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [search, setSearch] = useSearchParams();
  const tab = (search.get('tab') as TabId) || 'overview';
  const session = useSession();
  const navigate = useNavigate();

  const api = useMemo(
    () => new AdminApiClient({
      baseUrl: import.meta.env.VITE_LICENSE_SERVER_URL ?? '',
      getToken: () => session.state.token,
    }),
    [session.state.token],
  );

  const [detail, setDetail] = useState<LicenseDetailRow | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!id) return;
    void api.licenseDetail(id).then(setDetail).catch((e: unknown) => setErr(failureFrom(e, 'this license').message));
  }, [id, api, reloadKey]);

  const errMsg = (e: unknown): string =>
    userMessageFor(e);

  async function run(label: string, fn: () => Promise<string>): Promise<void> {
    setBusy(label); setMsg(null); setErr(null);
    try { setMsg(await fn()); setReloadKey((x) => x + 1); }
    catch (e) { setErr(errMsg(e)); }
    finally { setBusy(null); }
  }

  const perms = usePermissions();
  // Server: every write on this page is owner/admin only (Support and Read-only cannot).
  const canMutate = perms.canWrite;
  const status = detail?.status;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <button
          onClick={() => navigate('/licenses')}
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
        >
          ← Licenses
        </button>
        <h1 style={{ margin: 0, fontSize: '1.25rem' }}>
          License <code style={{ fontSize: '0.875rem' }}>{id.slice(0, 12)}…</code>
        </h1>
        {detail && (
          <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            {detail.company_name} · {detail.plan_tier} {detail.license_type} · {status}
          </span>
        )}
      </header>

      {err && <p role="alert" style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{err}</p>}
      {msg && <p role="status" style={{ color: 'var(--accent-text)', fontSize: '0.875rem' }}>{msg}</p>}
      <AccessNotice reason={canMutate ? null : `${perms.writeReason ?? ''} Pushing, suspending, restoring and revoking are not available to you.`} />

      {detail && canMutate && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Button
            variant="outline" size="sm"
            disabled={busy !== null}
            onClick={() => void run('refresh', async () => {
              await api.refreshLicense(detail.id);
              return 'License re-minted; engine will pick up on next heartbeat.';
            })}
          >
            {busy === 'refresh' ? 'Pushing…' : 'Push to engine now'}
          </Button>
          {(status === 'suspended' || status === 'past_due') && (
            <Button
              variant="outline" size="sm"
              disabled={busy !== null}
              onClick={() => void run('restore', async () => {
                const reason = prompt('Reason for restore (audit trail):') ?? '';
                if (!reason.trim()) throw new Error('reason is required');
                await api.restoreLicense(detail.id, reason);
                return 'License restored.';
              })}
            >
              {busy === 'restore' ? 'Restoring…' : 'Restore'}
            </Button>
          )}
          {status !== 'revoked' && status !== 'suspended' && (
            <Button
              variant="outline" size="sm"
              disabled={busy !== null}
              onClick={() => void run('suspend', async () => {
                const reason = prompt('Reason for suspend (audit trail):') ?? '';
                if (!reason.trim()) throw new Error('reason is required');
                await api.suspendLicense(detail.id, reason);
                return 'License suspended.';
              })}
            >
              {busy === 'suspend' ? 'Suspending…' : 'Suspend'}
            </Button>
          )}
          {status !== 'revoked' && (
            <Button
              variant="destructive" size="sm"
              disabled={busy !== null}
              onClick={() => void run('revoke', async () => {
                const reason = prompt('Reason for revoke (audit trail):') ?? '';
                if (!reason.trim()) throw new Error('reason is required');
                if (!confirm('Revoke is permanent. Continue?')) throw new Error('cancelled');
                await api.revokeLicense(detail.id, reason);
                return 'License revoked.';
              })}
            >
              {busy === 'revoke' ? 'Revoking…' : 'Revoke'}
            </Button>
          )}
        </div>
      )}

      <div
        role="tablist"
        aria-label="License sections"
        style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)' }}
        onKeyDown={(e) => {
          // Arrow / Home / End move between tabs (roving tabindex).
          const i = TABS.findIndex((t) => t.id === tab);
          let next = i;
          if (e.key === 'ArrowRight') next = (i + 1) % TABS.length;
          else if (e.key === 'ArrowLeft') next = (i - 1 + TABS.length) % TABS.length;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = TABS.length - 1;
          else return;
          e.preventDefault();
          const target = TABS[next]!;
          setSearch({ tab: target.id });
          requestAnimationFrame(() => document.getElementById(`license-tab-${target.id}`)?.focus());
        }}
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            id={`license-tab-${t.id}`}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            aria-controls="license-tabpanel"
            tabIndex={tab === t.id ? 0 : -1}
            onClick={() => setSearch({ tab: t.id })}
            style={{
              padding: '8px 12px',
              border: 'none',
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              background: 'none',
              color: tab === t.id ? 'var(--text)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id="license-tabpanel" aria-labelledby={`license-tab-${tab}`} tabIndex={0}>
        {tab === 'overview'   && <OverviewTab detail={detail} api={api} failed={!!err} canEdit={canMutate} />}
        {tab === 'capacity'   && <CapacityTab api={api} licenseId={id} />}
        {tab === 'heartbeats' && <HeartbeatsTab api={api} licenseId={id} />}
        {tab === 'lifecycle'  && <LifecycleTab api={api} licenseId={id} />}
      </div>
    </div>
  );
}

// ── Overview ────────────────────────────────────────────────────────────

function OverviewTab({ detail, api, failed, canEdit }: { detail: LicenseDetailRow | null; api: AdminApiClient; failed?: boolean; canEdit: boolean }): JSX.Element {
  if (!detail) return failed ? <></> : <p role="status" style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  const limits = detail as unknown as { limits_json?: Record<string, number> };
  const maxInstances = limits.limits_json?.max_instances;
  const maxLinuxUsers = limits.limits_json?.max_linux_users;
  return (
    <div style={{ display: 'grid', gap: 24, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
      <Card title="Customer">
        <KV label="Company">{detail.company_name}</KV>
        <KV label="Email">{detail.primary_email}</KV>
        <KV label="Customer ID"><code>{detail.customer_id}</code></KV>
        <KV label="Status">{detail.status}</KV>
      </Card>
      <Card title="License">
        <KV label="Tier">{detail.plan_tier}</KV>
        <KV label="Type">{detail.license_type}</KV>
        <KV label="Issued">{detail.issued_at.slice(0, 10)}</KV>
        <KV label="Expires">{detail.expires_at?.slice(0, 10) ?? '—'}</KV>
        <KV label="Activations">{detail.activation_count} / {detail.max_activations}</KV>
        <KV label="Activation key"><SecretValue value={detail.activation_key} redacted={detail.activation_key_redacted} /></KV>
      </Card>
      <Card title="Limits">
        <KV label="Max instances">{maxInstances === undefined ? 'Plan default' : String(maxInstances)}</KV>
        <KV label="Max hosting accounts">{maxLinuxUsers === undefined ? 'Plan default' : String(maxLinuxUsers)}</KV>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', margin: '8px 0 0' }}>
          Edits propagate to the engine on the next heartbeat (≤ 6h) or click "Push to engine now".
        </p>
      </Card>
      <Card title="Timing">
        <TimingCard detail={detail} api={api} canEdit={canEdit} />
      </Card>
      <Card title="Billing">
        <KV label="Stripe customer"><code style={{ fontSize: '0.75rem' }}>{detail.stripe_customer_id ?? '—'}</code></KV>
        <KV label="Subscription"><code style={{ fontSize: '0.75rem' }}>{detail.stripe_subscription_id ?? '—'}</code></KV>
      </Card>
    </div>
  );
}

// ── Capacity tab ────────────────────────────────────────────────────────

function CapacityTab({ api, licenseId }: { api: AdminApiClient; licenseId: string }): JSX.Element {
  const [data, setData] = useState<LicenseCapacityResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const lastRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastRef.current === licenseId) return;
    lastRef.current = licenseId;
    setData(null); setErr(null);
    void api.licenseCapacity(licenseId).then(setData).catch((e: unknown) => setErr(failureFrom(e, 'this license').message));
  }, [api, licenseId]);

  if (err) return <p role="alert" style={{ color: 'var(--danger)' }}>{err}</p>;
  if (!data) return <p style={{ color: 'var(--text-muted)' }}>Loading capacity history…</p>;
  if (data.snapshots.length === 0) {
    return <p style={{ color: 'var(--text-muted)' }}>No capacity snapshots in the last 30 days.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
        <Stat label="Latest instances"   value={data.latest?.instances_count   ?? 0} />
        <Stat label="Latest hosting accounts" value={data.latest?.linux_users_count ?? 0} />
        <Stat label="Instances p95"      value={data.aggregate.instances_p95}    />
        <Stat label="Hosting accounts max"    value={data.aggregate.linux_users_max}  />
      </div>

      <Card title="Recent snapshots">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: 6 }}>Captured</th>
              <th style={{ padding: 6 }}>Instances</th>
              <th style={{ padding: 6 }}>systemd</th>
              <th style={{ padding: 6 }}>docker</th>
              <th style={{ padding: 6 }}>vloud_app</th>
              <th style={{ padding: 6 }}>Hosting accounts</th>
            </tr>
          </thead>
          <tbody>
            {data.snapshots.slice(-50).reverse().map((s, i) => (
              <CapacityRow key={i} s={s} />
            ))}
          </tbody>
        </table>
        {data.aggregate.sample_count > 50 && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: 8 }}>
            Showing 50 of {data.aggregate.sample_count} snapshots in the window.
          </p>
        )}
      </Card>
    </div>
  );
}

function CapacityRow({ s }: { s: CapacitySnapshot }): JSX.Element {
  return (
    <tr style={{ borderBottom: '1px solid var(--border-subtle, var(--border))' }}>
      <td style={{ padding: 6, fontVariantNumeric: 'tabular-nums' }}>
        {s.captured_at ? new Date(s.captured_at * 1000).toISOString().replace('T', ' ').slice(0, 19) : '—'}
      </td>
      <td style={{ padding: 6 }}>{s.instances_count}</td>
      <td style={{ padding: 6 }}>{s.instances_systemd}</td>
      <td style={{ padding: 6 }}>{s.instances_docker}</td>
      <td style={{ padding: 6 }}>{s.instances_vloud_app}</td>
      <td style={{ padding: 6 }}>{s.linux_users_count}</td>
    </tr>
  );
}

// ── Heartbeats tab — installations + freshness ──────────────────────────
//
// One row per installation bound to the license. The locked model
// caps installations at 1 per license, but the engine's drift-rebind
// flow means transitional rows (status=offline / warning) can exist
// briefly. Showing all rows surfaces those transitions to the
// operator without needing to dig into raw heartbeats.

function HeartbeatsTab({ api, licenseId }: { api: AdminApiClient; licenseId: string }): JSX.Element {
  const [installs, setInstalls] = useState<Awaited<ReturnType<AdminApiClient['licenseInstallations']>> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const lastRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastRef.current === licenseId) return;
    lastRef.current = licenseId;
    setInstalls(null); setErr(null);
    void api.licenseInstallations(licenseId).then(setInstalls).catch((e: unknown) => setErr(failureFrom(e, 'this license').message));
  }, [api, licenseId]);

  if (err) return <p role="alert" style={{ color: 'var(--danger)' }}>{err}</p>;
  if (!installs) return <p style={{ color: 'var(--text-muted)' }}>Loading installations…</p>;
  if (installs.installations.length === 0) {
    return <p style={{ color: 'var(--text-muted)' }}>No installations on file. License has not been activated.</p>;
  }

  const now = Date.now();

  return (
    <Card title="Installations & heartbeat freshness">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
            <th style={{ padding: 6 }}>Installation</th>
            <th style={{ padding: 6 }}>OS</th>
            <th style={{ padding: 6 }}>Engine version</th>
            <th style={{ padding: 6 }}>Status</th>
            <th style={{ padding: 6 }}>Last heartbeat</th>
          </tr>
        </thead>
        <tbody>
          {installs.installations.map((i) => {
            const lastTs = i.last_heartbeat_at ? Date.parse(i.last_heartbeat_at) : null;
            const ageSec = lastTs ? Math.floor((now - lastTs) / 1000) : null;
            return (
              <tr key={i.id} style={{ borderBottom: '1px solid var(--border-subtle, var(--border))' }}>
                <td style={{ padding: 6 }}>
                  <code style={{ fontSize: '0.75rem' }}>{i.machine_fingerprint.slice(0, 16)}…</code>
                </td>
                <td style={{ padding: 6 }}>
                  {i.os_distro ?? '—'} {i.os_version ? `(${i.os_version})` : ''}
                </td>
                <td style={{ padding: 6, fontVariantNumeric: 'tabular-nums' }}>
                  {i.binary_version ?? '—'}
                </td>
                <td style={{ padding: 6 }}>
                  <StatusBadge status={i.status} />
                </td>
                <td style={{ padding: 6, fontVariantNumeric: 'tabular-nums' }}>
                  {ageSec === null
                    ? <span style={{ color: 'var(--text-muted)' }}>never</span>
                    : <HeartbeatAge seconds={ageSec} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p style={{ marginTop: 12, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
        Heartbeats are expected every 6h. After 7 missed heartbeats the engine
        transitions to <code>warning</code>; 14 missed → <code>degraded</code>;
        30 missed → <code>revoked</code>.
      </p>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const tone =
    status === 'active'    ? 'var(--accent)'
  : status === 'offline'   ? 'var(--text-muted)'
  : status === 'warning'   ? 'var(--warning, orange)'
  : status === 'degraded'  ? 'var(--danger, red)'
  : status === 'revoked'   ? 'var(--danger, red)'
  : status === 'suspended' ? 'var(--danger, red)'
  :                          'var(--text-muted)';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 6px', borderRadius: 3,
      background: 'var(--surface-soft, transparent)',
      border: `1px solid ${tone}`, color: tone,
      fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5,
    }}>
      {status}
    </span>
  );
}

function HeartbeatAge({ seconds }: { seconds: number }): JSX.Element {
  const tone = seconds > 30 * 24 * 3600 ? 'var(--danger, red)'
             : seconds >  7 * 24 * 3600 ? 'var(--warning, orange)'
             :                            'var(--text)';
  let label: string;
  if (seconds < 60)               label = `${seconds}s ago`;
  else if (seconds < 3600)        label = `${Math.floor(seconds / 60)}m ago`;
  else if (seconds < 86400)       label = `${Math.floor(seconds / 3600)}h ago`;
  else                            label = `${Math.floor(seconds / 86400)}d ago`;
  return <span style={{ color: tone }}>{label}</span>;
}

// ── Lifecycle tab ───────────────────────────────────────────────────────

function LifecycleTab({ api, licenseId }: { api: AdminApiClient; licenseId: string }): JSX.Element {
  const [events, setEvents] = useState<LifecycleEvent[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const lastRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastRef.current === licenseId) return;
    lastRef.current = licenseId;
    setEvents(null); setErr(null);
    void api.licenseLifecycle(licenseId)
      .then((r) => setEvents(r.events))
      .catch((e: unknown) => setErr(failureFrom(e, 'this license').message));
  }, [api, licenseId]);

  if (err) return <p role="alert" style={{ color: 'var(--danger)' }}>{err}</p>;
  if (!events) return <p style={{ color: 'var(--text-muted)' }}>Loading timeline…</p>;
  if (events.length === 0) return <p style={{ color: 'var(--text-muted)' }}>No lifecycle events yet.</p>;

  return (
    <Card title="Lifecycle timeline">
      <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {events.map((e, i) => (
          <LifecycleEventItem key={e.id ?? i} ev={e} />
        ))}
      </ol>
    </Card>
  );
}

const EVENT_TONE: Record<LifecycleEventType, string> = {
  activated:        'var(--accent)',
  heartbeat_ok:     'var(--text-muted)',
  secret_rotated:   'var(--text-muted)',
  plan_changed:     'var(--accent)',
  limits_changed:   'var(--accent)',
  tier_changed:     'var(--accent)',
  warning:          'var(--warning, orange)',
  degraded:         'var(--warning, orange)',
  suspended:        'var(--danger, red)',
  revoked:          'var(--danger, red)',
  expired:          'var(--danger, red)',
  restored:         'var(--accent)',
  heartbeat_failed: 'var(--warning, orange)',
};

function LifecycleEventItem({ ev }: { ev: LifecycleEvent }): JSX.Element {
  const tone = EVENT_TONE[ev.event_type] ?? 'var(--text-muted)';
  const ts = ev.occurred_at ? new Date(ev.occurred_at * 1000).toISOString().replace('T', ' ').slice(0, 19) : '—';
  return (
    <li style={{
      padding: '10px 0', borderBottom: '1px solid var(--border-subtle, var(--border))',
      display: 'grid', gridTemplateColumns: '160px 140px 1fr 100px', gap: 12, alignItems: 'baseline',
    }}>
      <code style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{ts}</code>
      <span style={{ color: tone, fontSize: '0.8125rem', fontWeight: 500 }}>{ev.event_type}</span>
      <span style={{ fontSize: '0.8125rem' }}>{ev.reason ?? <em style={{ color: 'var(--text-muted)' }}>—</em>}</span>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{ev.actor_type}{ev.actor_id ? ` · ${ev.actor_id.slice(0, 8)}` : ''}</span>
    </li>
  );
}

/**
 * The two clocks that decide how fast a status change reaches this engine
 * and how fast it acts on one.
 *
 * Both used to be unreachable from here: the check-in interval lived in the
 * engine's own environment, and the revoke grace was a hardcoded 24h in the
 * engine binary. Worst case a revoke took ~30h to bite — up to 6h before the
 * engine heard about it, then 24h before it acted.
 */
function TimingCard({ detail, api, canEdit }: { detail: LicenseDetailRow; api: AdminApiClient; canEdit: boolean }): JSX.Element {
  const [hb, setHb] = useState<string>(
    detail.heartbeat_interval_seconds == null ? '' : String(detail.heartbeat_interval_seconds),
  );
  const [grace, setGrace] = useState<string>(
    detail.revoke_grace_seconds == null ? '' : String(detail.revoke_grace_seconds),
  );
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // '' means "no override" → send null → fall back to the plan default.
  // Number('') is 0, which would silently mean "exit immediately", so the
  // empty case is handled before any numeric conversion.
  const parse = (v: string): number | null => (v.trim() === '' ? null : Number(v));

  const save = async (): Promise<void> => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.updateTiming(detail.id, {
        heartbeat_interval_seconds: parse(hb),
        revoke_grace_seconds: parse(grace),
        reason: 'changed from the licence panel',
      });
      setMsg(r.note);
    } catch (e) {
      setMsg(`Could not save: ${userMessageFor(e, 'the timing settings')}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Field
        label="Check-in interval (seconds)"
        placeholder="21600 — plan default (6h)"
        value={hb} onChange={setHb} readOnly={!canEdit}
        help={'How often this engine contacts the cloud. This is how long ANY change takes '
            + 'to reach it — revoke, suspend, resume, plan change. Minimum 60. Leave empty '
            + 'for the plan default. Lower values also shorten how long the engine tolerates '
            + 'being offline before it revokes itself.'}
      />
      <Field
        label="Revoke grace (seconds)"
        placeholder="86400 — plan default (24h)"
        value={grace} onChange={setGrace} readOnly={!canEdit}
        help={'After the engine LEARNS it is revoked, how long it keeps running before it '
            + 'shuts down. 0 means it exits on that same check-in. Note the engine still has '
            + 'to hear about the revocation first, so the shortest real delay is one '
            + 'check-in interval. Leave empty for the plan default.'}
      />
      <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', margin: '10px 0 8px' }}>
        A revoked engine keeps serving sites, databases and mail until it exits — and its data
        is never deleted. What stops is the control plane, so certificates stop renewing.
        Suspend is the reversible option; revoke has no undo.
      </p>
      {canEdit ? (
        <button className="btn" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save timing'}
        </button>
      ) : (
        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>
          Timing can be changed by Owner and Admin accounts.
        </p>
      )}
      {msg && <p role="status" style={{ fontSize: '0.75rem', marginTop: 8 }}>{msg}</p>}
    </div>
  );
}

function Field({ label, value, onChange, help, placeholder, readOnly }: {
  label: string; value: string; onChange: (v: string) => void;
  help: string; placeholder: string; readOnly?: boolean;
}): JSX.Element {
  const uid = useId();
  return (
    <div style={{ marginBottom: 12 }}>
      <label htmlFor={uid} style={{ display: 'block', fontSize: '0.8125rem', marginBottom: 4 }}>{label}</label>
      <input
        id={uid}
        aria-describedby={`${uid}-help`}
        type="number" min={0} value={value} placeholder={placeholder}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', padding: '6px 8px' }}
      />
      <p id={`${uid}-help`} style={{ color: 'var(--text-muted)', fontSize: '0.7rem', margin: '4px 0 0' }}>{help}</p>
    </div>
  );
}

// ── Local ui helpers ────────────────────────────────────────────────────

function Card({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section style={{
      border: '1px solid var(--border)', borderRadius: 6, padding: 16,
      background: 'var(--surface, transparent)',
    }}>
      <h2 style={{ margin: '0 0 12px', fontSize: '0.8125rem', textTransform: 'uppercase',
                   letterSpacing: 0.5, color: 'var(--text-muted)' }}>{title}</h2>
      {children}
    </section>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0',
                  borderBottom: '1px dashed var(--border-subtle, var(--border))' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{label}</span>
      <span style={{ fontSize: '0.8125rem', textAlign: 'right' }}>{children}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 12 }}>
      <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}
